import { useCallback } from "react";
import { create } from "zustand";
import { DateTime } from "luxon";
import { persist } from "zustand/middleware";
import * as Sentry from "@sentry/react";

import { type SurveyConfigVersion } from "@neuromotion-inc/mightier-survey-engine";
import { SurveyEngineIface } from "./useSurveyEngine";

/**
 * V0 - zustand plays very elegantly with versioning of persisted storage.
 * We need to keep the types for the old versions around for zustand's `migrate` feature
 * https://zustand.docs.pmnd.rs/integrations/persisting-store-data#migrate
 */

interface SurveyStateV0 {
  surveyId: string;
  version: SurveyConfigVersion;
  /**
   * Opaque string that is used to persist to storage. You should not JSON.parse this
   * ever, other than to call SurveyEngine.fromJsonRepresentation() with it.
   */
  surveyEngineJSONRepresentationStr: string;
}

interface SurveyStateStoreV0 {
  saveSurveyState: (engine: SurveyEngineIface) => void;
  surveyStates: SurveyStateV0[];
}

/**
 * V1 (persist startDate)
 */

interface SurveyInstanceV1 extends SurveyStateV0 {
  startedAt: string;
}

interface SurveyStateStoreV1 {
  saveSurveyState: (engine: SurveyEngineIface) => void;
  surveyInstances: SurveyInstanceV1[];
}

/**
 *
 */

const isExpired = (
  startedAtIsoStr: string,
  expirationDays: number | undefined,
): boolean => {
  if (!expirationDays) {
    return false;
  }
  const startedAt = DateTime.fromISO(startedAtIsoStr);
  const expiresAt = startedAt.plus({ days: expirationDays });
  return DateTime.local() > expiresAt;
};

class SurveyStateSetter {
  private _allPersistedSurveyStates: SurveyInstanceV1[];
  private _engine: SurveyEngineIface;

  constructor(
    allPersistedSurveyStates: SurveyInstanceV1[],
    surveyEngine: SurveyEngineIface,
  ) {
    this._allPersistedSurveyStates = allPersistedSurveyStates;
    this._engine = surveyEngine;
  }

  buildNewArrayOfSurveyStates(): SurveyInstanceV1[] {
    const rest = this._allPersistedSurveyStates.filter((instance) => {
      return !this._doesSurveyStateMatchEngine(instance);
    });

    // "current" means "currently being taken" (id/version match what the SurveyEngine is currently running)
    const currentlySavedInstance = this._getExistingSavedInstanceForEngine();

    const instanceToSave = this._assembleInstanceFromSurveyEngine(
      currentlySavedInstance,
    );

    // NOTE: order does not matter here (since we filter the array for lookup)
    // so we can just filter out and append the new one at the end
    // Adds new survey state for this surveyId and version
    return [...rest, instanceToSave];
  }

  private _doesSurveyStateMatchEngine(instance: SurveyInstanceV1): boolean {
    const idMatch = instance.surveyId === this._engine.surveyConfigId;
    const versionMatch = instance.version === this._engine.surveyConfigVersion;
    return idMatch && versionMatch;
  }

  private _getExistingSavedInstanceForEngine(): SurveyInstanceV1 | undefined {
    return this._allPersistedSurveyStates.find((instance) => {
      return this._doesSurveyStateMatchEngine(instance);
    });
  }

  private _assembleInstanceFromSurveyEngine(
    existingInstance: SurveyInstanceV1 | undefined,
  ): SurveyInstanceV1 {
    if (existingInstance) {
      return {
        ...existingInstance,
        surveyEngineJSONRepresentationStr: this._engine.toJsonRepresentation(),
      };
    }

    return {
      surveyEngineJSONRepresentationStr: this._engine.toJsonRepresentation(),
      surveyId: this._engine.surveyConfigId,
      version: this._engine.surveyConfigVersion,
      startedAt: DateTime.local().toISO(),
    };
  }
}

const CURRENT_PERSIST_SCHEMA_VERSION = 1;

// Returns object including an array of surveyStates, function to save surveyState
// exported for DEBUG ONLY don't use it in real code lol
export const _useSurveyStateZustandStore = create<SurveyStateStoreV1>()(
  persist(
    (set) => {
      return {
        surveyInstances: [],
        saveSurveyState: (engine: SurveyEngineIface): void => {
          set((currentState) => {
            const setter = new SurveyStateSetter(
              currentState.surveyInstances,
              engine,
            );

            return {
              ...currentState,
              surveyInstances: setter.buildNewArrayOfSurveyStates(),
            } satisfies SurveyStateStoreV1;
          });
        },
      };
    },
    {
      name: "survey-engine-state",
      version: CURRENT_PERSIST_SCHEMA_VERSION,
      migrate: (persisted, version): SurveyStateStoreV1 => {
        /**
         * V0 -> current version migration.
         * Need to set the startedAt property to something in the past, so that it can be used
         * in calculations.
         */
        if (version === 0) {
          const v0State = persisted as SurveyStateStoreV0;
          const { surveyStates, ...rest } = v0State;
          return {
            ...rest,
            surveyInstances: surveyStates.map((instancev0) => {
              return {
                ...instancev0,
                startedAt: DateTime.local().minus({ days: 30 }).toISO(),
              };
            }),
          };
        }

        // alert if we're unable to migrate - something is wrong
        if (version !== CURRENT_PERSIST_SCHEMA_VERSION) {
          Sentry.captureMessage(
            "Unrecognized survey local storage schema version",
            { extra: { configVersion: version } },
          );
        }

        // migration not triggered
        // @ts-expect-error things are messed up if we get this far - no way to assign typing. We have sentry logged.
        return persisted;
      },
    },
  ),
);

/**
 *
 */

type SurveyStateStoreReal = Pick<SurveyStateStoreV1, "saveSurveyState"> & {
  getSurveyState: (
    surveyId: string,
    version: SurveyConfigVersion,
    deleteCachedVersionAfterNDays: number | undefined,
  ) => SurveyInstanceV1 | undefined;
  deleteSurveyState: (surveyId: string, version: SurveyConfigVersion) => void;
};

export const useSurveyStateStore = (): SurveyStateStoreReal => {
  const { saveSurveyState, surveyInstances } = _useSurveyStateZustandStore();

  const deleteSurveyState = useCallback(
    (surveyId: string, version: SurveyConfigVersion) => {
      _useSurveyStateZustandStore.setState((currentState) => {
        return {
          surveyInstances: currentState.surveyInstances.filter(
            (surveyInstance: SurveyInstanceV1) => {
              return (
                surveyInstance.surveyId !== surveyId &&
                surveyInstance.version !== version
              );
            },
          ),
        };
      });
    },
    [],
  );

  // Function finds the desired survey state in store of all states based on survey ID and version
  const getSurveyState = useCallback(
    (
      surveyId: string,
      version: SurveyConfigVersion,
      deleteCachedVersionAfterNDays: number | undefined,
    ): SurveyInstanceV1 | undefined => {
      const ret = surveyInstances.find((surveyInstance: SurveyInstanceV1) => {
        return (
          surveyInstance.surveyId === surveyId &&
          surveyInstance.version === version
        );
      });

      // delete if it's expired - simply returning `undefined` isn't good enough
      if (ret && isExpired(ret.startedAt, deleteCachedVersionAfterNDays)) {
        deleteSurveyState(ret.surveyId, ret.version);
        return undefined;
      }

      return ret;
    },
    [surveyInstances, deleteSurveyState],
  );

  return { saveSurveyState, getSurveyState, deleteSurveyState };
};
